import React, { useCallback, useEffect, useState } from 'react';
import { authFetch, API } from './api';

const T = `${API}/admin-tasks`;

type Task = {
  id: number; title: string; detail: string | null; module: string | null;
  status: 'open' | 'in_progress' | 'waiting' | 'done';
  waiting_on: string | null; priority: 'high' | 'normal' | 'low';
  created_by: string | null; created_at: string;
  updated_by: string | null; updated_at: string;
  done_by: string | null; done_at: string | null;
};
type Module = { id: number; label: string; sort_order: number };

const STATUSES: { key: Task['status']; label: string; tone: string }[] = [
  { key: 'open',        label: 'To do',       tone: 'bg-gray-700 text-gray-200' },
  { key: 'in_progress', label: 'In progress', tone: 'bg-blue-600 text-white' },
  { key: 'waiting',     label: 'Waiting',     tone: 'bg-amber-600 text-white' },
  { key: 'done',        label: 'Done',        tone: 'bg-green-700 text-white' },
];
const PRIORITIES: { key: Task['priority']; label: string; tone: string }[] = [
  { key: 'high',   label: 'High',   tone: 'text-red-400' },
  { key: 'normal', label: 'Normal', tone: 'text-gray-400' },
  { key: 'low',    label: 'Low',    tone: 'text-gray-500' },
];

const statusMeta = (s: string) => STATUSES.find(x => x.key === s) || STATUSES[0];
const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString('et-EE') : '');

export default function AdminTasksPage() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [showDone, setShowDone] = useState(false);
  const [moduleFilter, setModuleFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // new-task form
  const [nTitle, setNTitle] = useState('');
  const [nDetail, setNDetail] = useState('');
  const [nModule, setNModule] = useState('');
  const [nStatus, setNStatus] = useState<Task['status']>('open');
  const [nWaiting, setNWaiting] = useState('');
  const [nPriority, setNPriority] = useState<Task['priority']>('normal');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (showDone) qs.set('status', 'done');
    if (moduleFilter) qs.set('module', moduleFilter);
    authFetch(`${T}?${qs.toString()}`)
      .then(r => (r.ok ? r.json() : r.json().then(j => Promise.reject(j.detail || 'Failed'))))
      .then(d => { setTasks(d.tasks || []); setCounts(d.counts || {}); setErr(''); })
      .catch(e => setErr(String(e)))
      .finally(() => setLoading(false));
  }, [showDone, moduleFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    authFetch(`${T}/modules`).then(r => r.json())
      .then(d => setModules(d.modules || [])).catch(() => {});
  }, []);

  const patch = (id: number, body: any) =>
    authFetch(`${T}/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(load).catch(() => setErr('Could not save the change'));

  const add = () => {
    if (!nTitle.trim()) return;
    setSaving(true);
    authFetch(T, {
      method: 'POST',
      body: JSON.stringify({
        title: nTitle.trim(), detail: nDetail.trim() || null,
        module: nModule || null, status: nStatus,
        waiting_on: nWaiting.trim() || null, priority: nPriority,
      }),
    })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(() => {
        setNTitle(''); setNDetail(''); setNWaiting('');
        setNStatus('open'); setNPriority('normal');
        load();
      })
      .catch(() => setErr('Could not add the task'))
      .finally(() => setSaving(false));
  };

  const remove = (t: Task) => {
    if (!window.confirm(`Delete "${t.title}"? This cannot be undone.`)) return;
    authFetch(`${T}/${t.id}`, { method: 'DELETE' })
      .then(r => (r.ok ? load() : Promise.reject()))
      .catch(() => setErr('Could not delete'));
  };

  return (
    <div className="p-6 text-gray-200">
      <div className="flex items-baseline gap-4 mb-1">
        <h1 className="text-2xl font-semibold text-white">To-do list</h1>
        <div className="flex gap-2 text-xs">
          {STATUSES.map(s => (
            <span key={s.key} className={`px-2 py-0.5 rounded ${s.tone}`}>
              {s.label} {counts[s.key] || 0}
            </span>
          ))}
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Shared backlog for admins — what still needs doing, and what is blocked on whom.
      </p>

      {err && <div className="mb-4 px-3 py-2 rounded bg-red-900/40 text-red-300 text-sm">{err}</div>}

      {/* ── add a task ─────────────────────────────────────────────── */}
      <div className="mb-6 p-4 rounded-lg bg-gray-800/60 border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <input
            className="md:col-span-5 bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm"
            placeholder="What needs doing?" value={nTitle}
            onChange={e => setNTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') add(); }} />
          <select className="md:col-span-2 bg-gray-900 border border-gray-700 rounded px-2 py-2 text-sm"
                  value={nModule} onChange={e => setNModule(e.target.value)}>
            <option value="">Module…</option>
            {modules.map(m => <option key={m.id} value={m.label}>{m.label}</option>)}
          </select>
          <select className="md:col-span-2 bg-gray-900 border border-gray-700 rounded px-2 py-2 text-sm"
                  value={nStatus} onChange={e => setNStatus(e.target.value as Task['status'])}>
            {STATUSES.filter(s => s.key !== 'done').map(s =>
              <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select className="md:col-span-1 bg-gray-900 border border-gray-700 rounded px-2 py-2 text-sm"
                  value={nPriority} onChange={e => setNPriority(e.target.value as Task['priority'])}>
            {PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          <button onClick={add} disabled={saving || !nTitle.trim()}
                  className="md:col-span-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700
                             disabled:text-gray-500 text-white rounded px-4 py-2 text-sm font-medium">
            {saving ? 'Adding…' : 'Add task'}
          </button>
          <input
            className="md:col-span-7 bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm"
            placeholder="Detail (optional)" value={nDetail}
            onChange={e => setNDetail(e.target.value)} />
          <input
            className="md:col-span-5 bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm"
            placeholder="Waiting on… (e.g. WGR — Ticket#2026081364017)" value={nWaiting}
            onChange={e => setNWaiting(e.target.value)} />
        </div>
      </div>

      {/* ── filters ────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3 mb-3 text-sm">
        <select className="bg-gray-900 border border-gray-700 rounded px-2 py-1.5"
                value={moduleFilter} onChange={e => setModuleFilter(e.target.value)}>
          <option value="">All modules</option>
          {modules.map(m => <option key={m.id} value={m.label}>{m.label}</option>)}
        </select>
        <label className="flex items-center gap-2 text-gray-400">
          <input type="checkbox" checked={showDone}
                 onChange={e => setShowDone(e.target.checked)} />
          Show done
        </label>
        <button onClick={load} className="text-gray-400 hover:text-white">Refresh</button>
      </div>

      {/* ── list ───────────────────────────────────────────────────── */}
      {loading ? (
        <div className="text-gray-500 text-sm">Loading…</div>
      ) : tasks.length === 0 ? (
        <div className="text-gray-500 text-sm py-8 text-center">
          {showDone ? 'Nothing completed yet.' : 'Nothing on the list. '}
        </div>
      ) : (
        <div className="space-y-2">
          {tasks.map(t => {
            const sm = statusMeta(t.status);
            const pr = PRIORITIES.find(p => p.key === t.priority)!;
            return (
              <div key={t.id}
                   className={`p-3 rounded-lg border flex flex-wrap items-start gap-3
                     ${t.status === 'waiting' ? 'bg-amber-950/20 border-amber-900/50'
                       : t.status === 'done' ? 'bg-gray-900/40 border-gray-800 opacity-70'
                       : 'bg-gray-800/40 border-gray-700'}`}>
                <input type="checkbox" className="mt-1"
                       checked={t.status === 'done'}
                       onChange={e => patch(t.id, { status: e.target.checked ? 'done' : 'open' })} />
                <div className="flex-1 min-w-[16rem]">
                  <div className={`font-medium ${t.status === 'done'
                      ? 'line-through text-gray-500' : 'text-white'}`}>
                    {t.title}
                  </div>
                  {t.detail && <div className="text-sm text-gray-400 mt-0.5">{t.detail}</div>}
                  {t.status === 'waiting' && t.waiting_on && (
                    <div className="text-xs text-amber-400 mt-1">⏳ waiting on {t.waiting_on}</div>
                  )}
                  <div className="text-xs text-gray-600 mt-1">
                    {t.module && <span className="mr-2">{t.module}</span>}
                    <span className={pr.tone}>{pr.label}</span>
                    <span className="mx-2">·</span>
                    {t.status === 'done' && t.done_at
                      ? <>done by {t.done_by} {fmt(t.done_at)}</>
                      : <>added by {t.created_by} {fmt(t.created_at)}</>}
                  </div>
                </div>
                <select value={t.status}
                        onChange={e => patch(t.id, { status: e.target.value })}
                        className={`text-xs rounded px-2 py-1 border-0 ${sm.tone}`}>
                  {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <select value={t.module || ''}
                        onChange={e => patch(t.id, { module: e.target.value || null })}
                        className="text-xs bg-gray-900 border border-gray-700 rounded px-2 py-1 text-gray-300">
                  <option value="">—</option>
                  {modules.map(m => <option key={m.id} value={m.label}>{m.label}</option>)}
                </select>
                <button onClick={() => remove(t)}
                        className="text-gray-600 hover:text-red-400 text-sm px-1"
                        title="Delete">×</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
